/**
 * Estado de Cuenta Suplidor — migración web de ares8535.
 *
 * Estado de cuenta de un suplidor de cuentas por pagar a una fecha de
 * corte: facturas con valor, pagado, balance, días y antigüedad de
 * saldo 0-30/31-60/61-90/+90. Exporta a Excel e imprime el estado de
 * cuenta con los datos de la compañía.
 *
 * Nota de migración: el balance queda unificado con el aging
 * (valor − pagado − avances); el escritorio ignoraba los avances en
 * este reporte.
 */
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — seleccione el suplidor y procese
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

// Estado de cuenta completo con encabezado de la compañía.
function imprimirEstado(compania, suplidor, fechacorte, facturas, totales) {
  const tr = facturas
    .map(
      (f) => `<tr>
        <td style="padding:3px 8px;border-bottom:1px solid #ddd">${f.nfactura}</td>
        <td style="padding:3px 8px;border-bottom:1px solid #ddd">${f.fecha}</td>
        <td style="padding:3px 8px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          f.valor
        )}</td>
        <td style="padding:3px 8px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          f.pagado
        )}</td>
        <td style="padding:3px 8px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          f.balance
        )}</td>
        <td style="padding:3px 8px;border-bottom:1px solid #ddd;text-align:right">${f.dias}</td>
      </tr>`
    )
    .join("");
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Estado de Cuenta Suplidor</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:12px">
      <h3 style="text-align:center;margin:0 0 2px">${compania?.nombre || ""}</h3>
      <p style="text-align:center;margin:0">${compania?.direccion || ""}</p>
      <p style="text-align:center;margin:0 0 10px">${
        compania?.rnc ? `RNC: ${compania.rnc}` : ""
      }</p>
      <p style="text-align:center;margin:0 0 2px;font-weight:bold;font-size:14px">Estado de Cuenta Suplidor</p>
      <p style="text-align:center;margin:0 0 14px;font-size:12px;color:#555">${
        suplidor?.descrip || ""
      }${suplidor?.rnc ? ` · RNC: ${suplidor.rnc}` : ""} · Corte al ${fechaDMY(fechacorte)}</p>
      <table style="width:100%;border-collapse:collapse">
        <thead><tr style="background:#eee">
          <th style="padding:4px 8px;text-align:left">Fact#</th>
          <th style="padding:4px 8px;text-align:left">Fecha</th>
          <th style="padding:4px 8px;text-align:right">Valor</th>
          <th style="padding:4px 8px;text-align:right">Pagado</th>
          <th style="padding:4px 8px;text-align:right">Balance</th>
          <th style="padding:4px 8px;text-align:right">Días</th>
        </tr></thead>
        <tbody>${tr}</tbody>
      </table>
      <p style="text-align:right;font-weight:bold;border-top:1px solid #000;padding-top:6px">
        Facturado: RD$ ${fmtMoney(totales?.valor)} ·
        Pagado: RD$ ${fmtMoney(totales?.pagado)} ·
        Balance: RD$ ${fmtMoney(totales?.balance)}
      </p>
      <p style="text-align:right;font-size:11px">
        0-30: RD$ ${fmtMoney(totales?.t0a30)} ·
        31-60: RD$ ${fmtMoney(totales?.t31a60)} ·
        61-90: RD$ ${fmtMoney(totales?.t61a90)} ·
        +90: RD$ ${fmtMoney(totales?.tmas90)}
      </p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

const moneyCol = (field, headerName, width = 110) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

export default function EstadoCuentaSuplidor() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [suplidores, setSuplidores] = useState([]);
  const [suplidor, setSuplidor] = useState(null);
  const [fechacorte, setFechacorte] = useState(hoyISO());

  const [facturas, setFacturas] = useState([]);
  const [totales, setTotales] = useState(null);
  const [compania, setCompania] = useState(null);
  const [procesado, setProcesado] = useState(null); // suplidor del último proceso
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    post("/api/cxp_suplidores", {}).then(({ ok, data }) => {
      if (ok) setSuplidores(data.suplidores || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const procesar = async () => {
    if (!suplidor) {
      showAlert({ message: "Seleccione el suplidor", color: "warning" });
      return;
    }
    setLoading(true);
    try {
      const { ok, data } = await post("/api/cxp_estadocuenta", {
        suplidor: suplidor.id,
        fechacorte,
      });
      if (ok) {
        setFacturas(data.facturas || []);
        setTotales(data.totales || null);
        setCompania(data.compania || null);
        setProcesado(suplidor);
      } else {
        showAlert({
          message: data.Error || "No se pudo generar el estado de cuenta",
          color: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const subtitulo = `${procesado?.descrip || ""} · Corte al ${fechaDMY(fechacorte)}`;

  const exportar = () => {
    const hoja = XLSX.utils.aoa_to_sheet([
      ["Estado de Cuenta Suplidor"],
      [subtitulo],
      [],
      ["Fact#", "Fecha", "Valor", "Pagado", "Balance", "Días"],
      ...facturas.map((f) => [f.nfactura, f.fecha, f.valor, f.pagado, f.balance, f.dias]),
      [],
      ["", "TOTAL", totales?.valor || 0, totales?.pagado || 0, totales?.balance || 0, ""],
    ]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Estado de Cuenta");
    XLSX.writeFile(libro, `EstadoCuentaSuplidor_${hoyISO()}.xlsx`);
  };

  const imprimir = () => {
    imprimirEstado(compania, procesado, fechacorte, facturas, totales);
  };

  const columnasFacturas = [
    { field: "nfactura", headerName: "Fact#", width: 110 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    moneyCol("valor", "Valor", 125),
    moneyCol("pagado", "Pagado", 125),
    moneyCol("balance", "Balance", 125),
    { field: "dias", headerName: "Días", width: 80, align: "right", headerAlign: "right" },
    moneyCol("t0a30", "0-30", 110),
    moneyCol("t31a60", "31-60", 110),
    moneyCol("t61a90", "61-90", 110),
    moneyCol("tmas90", "+90", 110),
  ];

  const hayDatos = facturas.length > 0;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Estado de Cuenta Suplidor"
        subtitle="Facturas pendientes del suplidor con antigüedad de saldo a la fecha de corte"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={5} md={4}>
              <Autocomplete
                options={suplidores}
                value={suplidor}
                onChange={(_, v) => setSuplidor(v)}
                getOptionLabel={(o) => o?.descrip || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Suplidor" sx={fpInputSx} />}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Fecha de corte"
                InputLabelProps={{ shrink: true }}
                value={fechacorte}
                onChange={(e) => setFechacorte(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip label="FACTURADO" value={`RD$ ${fmtMoney(totales?.valor)}`} />
            <ResumenChip
              label="PAGADO"
              value={`RD$ ${fmtMoney(totales?.pagado)}`}
              color={FP.color.success}
            />
            <ResumenChip label="BALANCE" value={`RD$ ${fmtMoney(totales?.balance)}`} />
            <ResumenChip
              label="0-30 DÍAS"
              value={`RD$ ${fmtMoney(totales?.t0a30)}`}
              color="#2E7D32"
            />
            <ResumenChip
              label="31-60 DÍAS"
              value={`RD$ ${fmtMoney(totales?.t31a60)}`}
              color="#F9A825"
            />
            <ResumenChip
              label="61-90 DÍAS"
              value={`RD$ ${fmtMoney(totales?.t61a90)}`}
              color="#EF6C00"
            />
            <ResumenChip
              label="+90 DÍAS"
              value={`RD$ ${fmtMoney(totales?.tmas90)}`}
              color="#C62828"
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={facturas}
              columns={columnasFacturas}
              getRowId={(r) => r.id}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
